// Libs
import classNames from 'classnames/bind';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid2, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseDatePicker, BaseInput, BaseSelect } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamUser } from '~/utils/interface/user';
import { IQueryParamVendors } from '~/utils/interface/vendors';
import { IFormPurchaseOrder } from '~/utils/interface/purchaseOrder';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import { formatInputCurrency, getFullName } from '~/utils/helper/common';
import { getListUser } from '~/thunks/user/userThunk';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  DOLLAR,
  EMPTY_STRING,
  optionMethodDelivery,
  optionTypePO,
} from '~/utils/constants/common';
import { getListVendors } from '~/thunks/vendors/vendorsThunk';
// Styles, images, icons
import styles from './PurchaseOrderInfoForm.module.scss';

type Props = {
  vendorNameDefault?: string;
  approvedByNameDefault?: string;
};

const cx = classNames.bind(styles);

const PurchaseOrderInfoForm = (props: Props) => {
  //#region Destructuring Props
  const { vendorNameDefault, approvedByNameDefault } = props;
  //#endregion Destructuring Props

  //#region Declare
  const { t } = useTranslation(['admin/purchaseOrder', 'common/base']);
  const dispatch = useAppDispatch();

  const {
    control,
    formState: { errors },
  } = useFormContext<IFormPurchaseOrder>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptionUser = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionVendor = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamVendors = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListVendors(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: item?.vendorName || EMPTY_STRING,
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionsMethod = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionMethodDelivery,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTypePO,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Grid2 id='purchaseOrderInfoFormComponent' columnSpacing={'24px'} rowSpacing={'24px'} container>
      <Grid2 size={6}>
        <Controller
          control={control}
          name={'type'}
          render={({ field: { onChange, value } }) => {
            return (
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('purchase_order_form_title_type')}
                callApiFn={fetchOptionsType}
                value={value}
                onChange={(data) => onChange(data?.value)}
                errorMessage={errors?.type?.message}
              />
            );
          }}
        />
      </Grid2>

      <Grid2 size={6} />

      <Grid2 size={6}>
        <Controller
          name={'vendorId'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <BaseSelect
              isSearch
              gap={8}
              styleType='contained'
              required
              value={value}
              onChange={(data) => {
                onChange(data?.value);
              }}
              type='loadMore'
              label={t('purchase_order_form_title_vendor_name')}
              callApiFn={fetchOptionVendor}
              itemNameDefault={vendorNameDefault}
              errorMessage={errors.vendorId?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          control={control}
          name='neededByDate'
          render={({ field: { onChange, value } }) => (
            <BaseDatePicker
              label={t('purchase_order_form_title_needed_by')}
              value={value}
              onChange={onChange}
              errorMessage={errors.neededByDate?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          name={'approvedBy'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <BaseSelect
              isSearch
              gap={8}
              styleType='contained'
              value={value}
              onChange={(data) => {
                onChange(data?.value);
              }}
              type='loadMore'
              label={t('purchase_order_form_title_approve_by')}
              callApiFn={fetchOptionUser}
              itemNameDefault={approvedByNameDefault}
              errorMessage={errors.approvedBy?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          control={control}
          name={'method'}
          render={({ field: { onChange, value } }) => {
            return (
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('purchase_order_form_title_method')}
                callApiFn={fetchOptionsMethod}
                value={value}
                onChange={(data) => onChange(data?.value)}
                errorMessage={errors?.method?.message}
              />
            );
          }}
        />
      </Grid2>

      <Grid2 size={12}>
        <Controller
          control={control}
          name={'approveImmediately'}
          render={({ field: { onChange, value } }) => {
            return (
              <BaseCheckbox
                label={t('purchase_order_form_title_approve_immediately')}
                checked={value}
                onChange={(value) => onChange(value)}
              />
            );
          }}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          control={control}
          name={'shippingPrice'}
          render={({ field: { onChange, value } }) => (
            <BaseInput
              label={t('purchase_order_form_title_shipping_price')}
              placeholder={t('common_text_placeholder_currency', { ns: 'common/base' })}
              value={value}
              startAdornment={<Typography className={cx('startAdornment')}>{DOLLAR}</Typography>}
              onChange={(event) => {
                onChange(formatInputCurrency(event.target.value));
              }}
            />
          )}
        />
      </Grid2>

      <Grid2 size={6}>
        <Controller
          control={control}
          name={'note'}
          render={({ field: { onChange, value } }) => {
            return (
              <BaseInput
                label={t('purchase_order_form_title_notes')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.note?.message}
              />
            );
          }}
        />
      </Grid2>
    </Grid2>
  );
};

export default PurchaseOrderInfoForm;
